import { type ReactNode, useId, useRef, useState } from "react";
import { UploadIcon } from "./icons";
import { cn } from "@/shared/lib/cn";

export interface FileRejection { file: File; reason: "type" | "size" | "count" }
export interface FileDropzoneProps {
  label: string;
  "aria-label"?: string;
  description?: string;
  accept?: string;
  multiple?: boolean;
  maxFileSize?: number;
  disabled?: boolean;
  error?: string;
  className?: string;
  /** Open an asset library instead of the local picker; dropping files still works. */
  onBrowse?: () => void;
  preview?: ReactNode;
  /** Decorative upload icon; defaults to the shared upload glyph. */
  icon?: ReactNode;
  onFilesSelected: (files: File[]) => void;
  onFilesRejected?: (files: FileRejection[]) => void;
}

/** A local file picker. The caller owns upload, cancellation, and persistence. */
export function FileDropzone({ label, description, accept, multiple = false,
  "aria-label": ariaLabel, maxFileSize, disabled = false, error, className, onFilesSelected, onFilesRejected, onBrowse, preview, icon,
}: FileDropzoneProps) {
  const id = useId();
  const dragDepth = useRef(0);
  const [dragging, setDragging] = useState(false);
  const [rejection, setRejection] = useState("");
  const message = error || rejection;
  const describedBy = [description && !preview && `${id}-description`, message && `${id}-error`].filter(Boolean).join(" ") || undefined;
  function select(files: File[]) {
    if (disabled || !files.length) return;
    const rejected: FileRejection[] = [];
    const accepted: File[] = [];
    for (const file of files) {
      const reason = !multiple && files.length > 1 ? "count"
        : !accepts(file, accept) ? "type"
        : maxFileSize !== undefined && file.size > maxFileSize ? "size" : undefined;
      if (reason) rejected.push({ file, reason });
      else accepted.push(file);
    }
    setRejection(rejected.map(({ file, reason }) =>
      `${file.name}: ${reason === "count" ? "choose one file at a time." : reason === "type" ? "this file type is not supported." : "this file exceeds the size limit."}`,
    ).join(" "));
    if (rejected.length) onFilesRejected?.(rejected);
    if (accepted.length) onFilesSelected(accepted);
  }
  const content = preview ?? <>
    <span className="studio-dropzone-icon" aria-hidden="true">{icon ?? <UploadIcon />}</span>
    <span className="studio-dropzone-label">{label}</span>
    {description ? <span id={`${id}-description`} className="studio-control-description">{description}</span> : null}
  </>;
  return (
    <div className={cn("studio-dropzone", className)} data-dragging={dragging && !disabled || undefined} data-disabled={disabled || undefined}
      onDragEnter={(event) => {
        if (!event.dataTransfer.types.includes("Files")) return;
        event.preventDefault(); dragDepth.current += 1;
        if (!disabled) setDragging(true);
      }}
      onDragOver={(event) => {
        if (!event.dataTransfer.types.includes("Files")) return;
        event.preventDefault(); event.dataTransfer.dropEffect = disabled ? "none" : "copy";
      }}
      onDragLeave={() => {
        dragDepth.current = Math.max(0, dragDepth.current - 1);
        if (!dragDepth.current) setDragging(false);
      }}
      onDrop={(event) => {
        event.preventDefault(); dragDepth.current = 0; setDragging(false);
        select(Array.from(event.dataTransfer.files));
      }}
    >
      {onBrowse ? <button type="button" className="studio-dropzone-target w-full" aria-label={ariaLabel ?? label} aria-describedby={describedBy} disabled={disabled} onClick={onBrowse}>{content}</button> : <label className="studio-dropzone-target">
        <input type="file" aria-label={ariaLabel ?? label} aria-describedby={describedBy} aria-invalid={message ? true : undefined}
          className="sr-only" accept={accept} multiple={multiple} disabled={disabled}
          onChange={(event) => { select(Array.from(event.currentTarget.files ?? [])); event.currentTarget.value = ""; }} />
        {content}
      </label>}
      <p id={`${id}-error`} role="status" className={message ? "studio-control-error" : "sr-only"}>{message}</p>
    </div>
  );
}

function accepts(file: File, accept?: string): boolean {
  if (!accept?.trim()) return true;
  return accept.split(",").some((item) => {
    const pattern = item.trim().toLowerCase();
    if (!pattern) return false;
    if (pattern.startsWith(".")) return file.name.toLowerCase().endsWith(pattern);
    if (pattern.endsWith("/*")) return file.type.toLowerCase().startsWith(pattern.slice(0, -1));
    return file.type.toLowerCase() === pattern;
  });
}
